import React from 'react';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Switch } from '../../../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Edit, Trash2, Loader2, Tag, Calendar, Users } from 'lucide-react';
import { PromoCodesTableProps } from '../types';

export function PromoCodesTable({
  isDark,
  promoCodes,
  isLoading,
  onEdit,
  onDelete,
  onToggleActive,
  formatDate,
  formatValue,
}: PromoCodesTableProps) {
  const isExpired = (validUntil: string | null) => {
    if (!validUntil) return false;
    return new Date(validUntil) < new Date();
  };

  const isNotYetValid = (validFrom: string | null) => {
    if (!validFrom) return false;
    return new Date(validFrom) > new Date();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#0EA5A4]" />
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className={cn(
          isDark ? 'border-slate-700 hover:bg-slate-800/50' : 'border-slate-100 hover:bg-slate-50'
        )}>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Code</TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Type</TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Value</TableHead>
          <TableHead className={cn('font-semibold hidden lg:table-cell', isDark ? 'text-slate-400' : 'text-slate-500')}>Usage</TableHead>
          <TableHead className={cn('font-semibold hidden md:table-cell', isDark ? 'text-slate-400' : 'text-slate-500')}>Valid Period</TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Status</TableHead>
          <TableHead className={cn('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {promoCodes.length === 0 ? (
          <TableRow>
            <TableCell colSpan={7} className="text-center py-12 text-slate-500">
              No promo codes found
            </TableCell>
          </TableRow>
        ) : (
          promoCodes.map((promoCode) => {
            const expired = isExpired(promoCode.valid_until);
            const notYetValid = isNotYetValid(promoCode.valid_from);
            const usageLimitReached = promoCode.max_uses !== null && promoCode.used_count >= promoCode.max_uses;
            // Блокирующая причина — даже если is_active=true, промокод не сработает.
            const blockReason = expired
              ? 'Expired'
              : usageLimitReached
              ? 'Limit reached'
              : notYetValid
              ? 'Not yet valid'
              : null;

            return (
              <TableRow
                key={promoCode.id}
                className={cn(
                  'transition-colors',
                  isDark
                    ? 'border-slate-700 hover:bg-slate-800/50'
                    : 'border-slate-100 hover:bg-slate-50',
                  !promoCode.is_active && 'opacity-60',
                )}
              >
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Tag className={cn('w-4 h-4', isDark ? 'text-slate-400' : 'text-slate-500')} />
                    <span className={cn('font-mono text-sm font-semibold', isDark ? 'text-white' : 'text-[#0F172A]')}>
                      {promoCode.code}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge className={cn(
                    'rounded-lg font-medium border-0',
                    promoCode.type === 'percent' ? 'bg-blue-500/10 text-blue-500' : 'bg-purple-500/10 text-purple-500'
                  )}>
                    {promoCode.type === 'percent' ? 'Percent' : 'Fixed'}
                  </Badge>
                </TableCell>
                <TableCell className={cn('font-medium', isDark ? 'text-white' : 'text-[#0F172A]')}>
                  {formatValue(promoCode.type, promoCode.value)}
                </TableCell>
                <TableCell className={cn('hidden lg:table-cell', isDark ? 'text-slate-300' : 'text-slate-700')}>
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    <span>{promoCode.used_count}{promoCode.max_uses !== null ? ` / ${promoCode.max_uses}` : ' / ∞'}</span>
                  </div>
                </TableCell>
                <TableCell className={cn('hidden md:table-cell text-xs', isDark ? 'text-slate-400' : 'text-slate-500')}>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>{formatDate(promoCode.valid_from)} - {formatDate(promoCode.valid_until)}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col items-start gap-1.5">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={promoCode.is_active}
                        onCheckedChange={() => onToggleActive(promoCode)}
                      />
                      <span
                        className={cn(
                          'text-xs font-medium',
                          promoCode.is_active
                            ? 'text-emerald-500'
                            : isDark
                            ? 'text-slate-500'
                            : 'text-slate-400',
                        )}
                      >
                        {promoCode.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {blockReason && promoCode.is_active && (
                      <Badge
                        className={cn(
                          'rounded-md text-[10px] px-1.5 py-0 h-4 font-medium border-0',
                          expired
                            ? 'bg-red-500/10 text-red-500'
                            : usageLimitReached
                            ? 'bg-orange-500/10 text-orange-500'
                            : 'bg-amber-500/10 text-amber-500',
                        )}
                      >
                        {blockReason}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => onEdit(promoCode)}
                      className={cn('h-8 px-2 rounded-lg', isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-white' : 'text-slate-600 hover:bg-slate-100')}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => onDelete(promoCode.id)}
                      className={cn('h-8 px-2 rounded-lg', isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-red-400' : 'text-slate-600 hover:bg-slate-100 hover:text-red-600')}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
